import { useState, useMemo } from "react";
import { useColorMode, Button } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";

import { getKpiPermissions } from "../../utils/permissions";
import MetricForm from "./MetricForm";
import MetricTable from "./MetricTable";
import UserManagement from "./Users/UserManagement";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string }[];
  organizationId: string;
  permissions?: "Admin" | "Editor" | "Viewer";
};

interface Metric {
  id: number;
  metric: string;
  description?: string;
  target: string;
  actual: string;
  status: string;
  mainId?: string;
  assignedTo: string[];
}

interface KpiMetricMainProps {
  organizationId: string;
  users: User[];
  color: string;
  bg: string;
  isAdmin: boolean;
  currentUserId: string;
  /** True organization admin (organization.admin === currentUserId) — bypasses the KPI-specific permission field entirely. */
  isOrgAdmin?: boolean;
}

const KpiMetricMain: React.FC<KpiMetricMainProps> = ({
  organizationId,
  users: usersProp,
  color,
  bg,
  isAdmin,
  currentUserId,
  isOrgAdmin = false,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Tag the logged-in user's entry with isOrgAdmin so every KPI permission check
  // (here and in child components deriving currentUser from this same users list)
  // bypasses the KPI-specific permissions field for the true organization admin.
  const users = useMemo(
    () =>
      isOrgAdmin
        ? usersProp.map((u) =>
            u.id === currentUserId ? { ...u, isOrgAdmin: true } : u
          )
        : usersProp,
    [usersProp, currentUserId, isOrgAdmin]
  );

  const currentUser = useMemo(
    () => users.find((u) => u.id === currentUserId),
    [users, currentUserId]
  );
  const kpiPerms = useMemo(() => getKpiPermissions(currentUser), [currentUser]);

  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Users, 1 = KPI
  // User selection state lifted up
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const handleUserSelect = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };
  const removeAssignedUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((id) => id !== userId));
  };
  const addMetric = (newMetric: Omit<Metric, "id">) => {
    setMetrics([...metrics, { ...newMetric, id: Date.now() }]);
  };

  const updateMetric = (updatedMetric: Metric) => {
    setMetrics(
      metrics.map((metric) =>
        metric.id === updatedMetric.id ? updatedMetric : metric
      )
    );
  };

  const deleteMetric = (metricId: number) => {
    setMetrics(metrics.filter((metric) => metric.id !== metricId));
  };

  return (
    <>
      {/* Header - standalone, same as Projects */}
      <div className={`flex-shrink-0 flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex items-center justify-between w-full relative">
          <div className="flex flex-col space-y-1">
            <span className={`text-2xl font-bold ${theme.text}`}>KPI Metrics Management</span>
            <span className={`text-xs ${theme.textMuted}`}>Start by adding and managing KPIs to empower enterprise oversight and performance tracking.</span>
          </div>
          {kpiPerms.isAdmin && (
            <div className="flex gap-6">
              <button
                className={`px-4 py-2 text-base font-medium outline-none transition-all relative
                  ${activeTab === 0 
                    ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 ${isDark ? 'after:bg-[#B59143]' : 'after:bg-[#B59143]'}` 
                    : `${isDark ? 'text-gray-300 hover:text-[#FBD282]' : 'text-gray-500 hover:text-[#B59143]'}`}
                `}
                onClick={() => setActiveTab(0)}
              >
                Users
              </button>
              <button
                className={`px-4 py-2 text-base font-medium outline-none transition-all relative
                  ${activeTab === 1 
                    ? `${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 ${isDark ? 'after:bg-[#B59143]' : 'after:bg-[#B59143]'}` 
                    : `${isDark ? 'text-gray-300 hover:text-[#FBD282]' : 'text-gray-500 hover:text-[#B59143]'}`}
                `}
                onClick={() => setActiveTab(1)}
              >
                KPI
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table Content - same structure as Projects */}
      {kpiPerms.isEditor || activeTab === 0 ? (
        <UserManagement
          users={users}
          color={color}
          bg={bg}
          organizationId={organizationId}
          isAdmin={kpiPerms.isAdmin}
          isEditor={kpiPerms.isEditor}
          canCreate={kpiPerms.canCreate}
          currentUserId={currentUserId}
        />
      ) : (
        <div className={`w-full rounded-md border ${theme.border} ${theme.card}`}>
          <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'} rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col`}>
            <MetricForm
              key="metric-form"
              addMetric={addMetric}
              users={users}
              color={color}
              bg={bg}
              organizationId={organizationId}
              isUserSelectOpen={isUserSelectOpen}
              setIsUserSelectOpen={setIsUserSelectOpen}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              handleUserSelect={handleUserSelect}
              removeAssignedUser={removeAssignedUser}
              isAdmin={kpiPerms.isAdmin}
            />
            <MetricTable
              metrics={metrics}
              updateMetric={updateMetric}
              deleteMetric={deleteMetric}
              users={users}
              color={color}
              bg={bg}
              organizationId={organizationId}
              setMetrics={setMetrics}
              isAdmin={kpiPerms.isAdmin}
              currentUserId={currentUserId}
            />
          </div>
        </div>
      )}

      {/* User Selection Modal Overlay */}
      {isUserSelectOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] text-white' : 'bg-white text-gray-900'} rounded-lg w-full max-w-md mx-4`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <h3 className="font-bold text-base">Select Users</h3>
              <button
                className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'} text-base`}
                onClick={() => setIsUserSelectOpen(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <ul className="space-y-2">
                {users.map((user) => (
                  <li
                    key={user.id}
                    className={`flex items-center justify-between gap-3 border-l-4 rounded-lg px-4 py-2 min-h-0 transition-all cursor-pointer shadow-sm ${isDark ? 'border-[#B59143] bg-[#1A1C1F] hover:bg-[#2A3441]' : 'border-[#E8C16C] bg-white hover:bg-[#FFF7E6]'}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-base leading-tight font-normal">{user.firstName} {user.lastName}</span>
                      <span className={`text-sm leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</span>
                    </div>
                    <input
                      type="checkbox"
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      className="accent-[#B59143] h-5 w-5 rounded ml-4"
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className={`flex justify-end px-4 py-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <Button
                onClick={() => setIsUserSelectOpen(false)}
                bgGradient="linear(to-r, #FBD282, #B59143)"
                color="white"
                fontWeight={700}
                _hover={{ bgGradient: 'linear(to-r, #E8C16C, #A7843D)', boxShadow: '0 4px 12px rgba(181, 145, 67, 0.35)' }}
                _active={{ transform: 'translateY(0)' }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KpiMetricMain;
